import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { PatientResponse } from "@/lib/types";
import api from "@/lib/api";
import { useOrganizationsStore } from "./organizationsStore";
import { persist } from "zustand/middleware";

interface PatientsState {
  patients: PatientResponse[];
  selectedPatients: PatientResponse[];
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  pageSize: number;
  sortingState: { id: string; desc: boolean }[];
  lastFetchTime: number;
  totalPatients: number;
  totalPages: number;
  currentOrganizationId: string | null;
  filters: {
    search: string | null;
    gender: string | null;
    tags: string[] | null;
    apcm_consent_status: string[] | null;
    latest_call_status: string[] | null;
  };
  // V3 Smart Search State
  isSmartSearchMode: boolean;
  smartSearchQuery: string | null;
  smartSearchFilters: any | null;
  // Structured filter-builder filters (persisted so they survive navigating to a patient and back)
  structuredFilters: any | null;
  queryStats: {
    patient_count: number;
    avg_success_rate: number;
    consent_pending_count: number;
    overdue_count: number;
  } | null;
  // Get patients for the currently selected organization
  fetchPatientsForCurrentOrg: () => Promise<void>;
  // Fetch patients with advanced filtering and server-side pagination
  fetchPatientsAdvanced: (organizationId: string) => Promise<void>;
  // Fetch patients using v3 smart search endpoint
  fetchPatientsV3: (organizationId: string, v3Filters: any) => Promise<void>;
  // Switch to smart search mode with v3 filters
  applySmartSearch: (query: string, v3Filters: any, results: any) => void;
  // Clear smart search and return to regular filtering
  clearSmartSearch: () => void;
  // Set NL smart search (clears structured filters)
  setSmartSearch: (query: string, v3Filters: any) => void;
  // Set structured filters (clears smart search)
  setStructuredFilters: (v3Filters: any | null) => void;
  // Add a new patient to the store
  addPatient: (patient: PatientResponse) => void;
  // Update a patient in the store
  updatePatient: (
    patientId: string,
    updatedPatient: Partial<PatientResponse>
  ) => void;
  // Add tags to multiple patients
  addTagsToMultiplePatients: (
    patientIds: string[],
    tags: string[]
  ) => Promise<void>;
  // Update language for multiple patients
  updatePatientsAfterBulkLanguageChange: (
    patientIds: string[],
    language: string
  ) => void;
  // Get a specific patient by ID
  getPatient: (patientId: string) => PatientResponse | undefined;
  // Clear patients (e.g., when logging out)
  clearPatients: () => void;
  // Set current page
  setCurrentPage: (page: number) => void;
  // Set sorting state
  setSortingState: (sorting: { id: string; desc: boolean }[]) => void;
  // Set page size
  setPageSize: (size: number) => void;
  // Set filters for server-side filtering
  setFilters: (filters: Partial<PatientsState["filters"]>) => void;
  // Get next patient ID
  getNextPatientId: (currentPatientId: string) => string | undefined;
  // Get previous patient ID
  getPreviousPatientId: (currentPatientId: string) => string | undefined;
  // Get total page count
  getPageCount: () => number;
  // Selection management
  togglePatientSelection: (patient: PatientResponse) => void;
  isPatientSelected: (patientId: string) => boolean;
  removePatientSelection: (patientId: string) => void;
  clearSelections: () => void;
  // Reset state when organization changes
  resetPaginationState: () => void;
}

export const usePatientsStore = create<PatientsState>()(
  persist(
    immer<PatientsState>((set, get) => ({
      patients: [],
      selectedPatients: [],
      isLoading: false,
      error: null,
      currentPage: 0,
      pageSize: 20,
      sortingState: [],
      lastFetchTime: 0,
      totalPatients: 0,
      totalPages: 0,
      currentOrganizationId: null,
      filters: {
        search: null,
        gender: null,
        tags: null,
        apcm_consent_status: null,
        latest_call_status: null,
      },
      // V3 Smart Search State
      isSmartSearchMode: false,
      smartSearchQuery: null,
      smartSearchFilters: null,
      structuredFilters: null,
      queryStats: null,


      fetchPatientsAdvanced: async (organizationId: string) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
          state.currentOrganizationId = organizationId;
        });

        try {
          // Convert sorting state to format expected by API
          const sortingState = get().sortingState;
          let sortBy = "created_at"; // default
          let sortOrder = "desc"; // default

          if (sortingState.length > 0) {
            // Handle multi-column sorting by joining column ids with commas
            sortBy = sortingState.map((sort) => sort.id).join(",");
            sortOrder = sortingState
              .map((sort) => (sort.desc ? "desc" : "asc"))
              .join(",");
          }

          const filters = get().filters;
          const currentPage = get().currentPage;
          const pageSize = get().pageSize;

          // Prepare array filters as comma-separated strings if present
          const tagsParam = filters.tags ? filters.tags.join(",") : undefined;
          const consentStatusParam = filters.apcm_consent_status
            ? filters.apcm_consent_status.join(",")
            : undefined;
          const callStatusParam = filters.latest_call_status
            ? filters.latest_call_status.join(",")
            : undefined;

          const response = await api.getPatientsAdvancedV3(organizationId, {
            page: currentPage + 1, // API is 1-indexed, TanStack Table is 0-indexed
            page_size: pageSize,
            sort_by: sortBy,
            sort_order: sortOrder,
            // Map v2 filter names to v3 parameter names
            patient_name: filters.search || undefined,
            patient_gender: filters.gender || undefined,
            include_tags: tagsParam,
            consent_status: consentStatusParam,
            call_statuses: callStatusParam,
          });

          set((state) => {
            state.patients = response.results;
            state.totalPatients = response.total;
            state.totalPages = response.total_pages || Math.ceil(response.total / response.page_size);
            
            // Store query stats if available
            if (response.query_summary) {
              state.queryStats = {
                patient_count: response.query_summary.patient_count,
                avg_success_rate: response.query_summary.avg_success_rate,
                consent_pending_count: response.query_summary.consent_pending_count,
                overdue_count: response.query_summary.overdue_count,
              };
            }

            // Ensure current page is valid for the new data set
            const maxPageIndex = Math.max(0, state.totalPages - 1);
            if (state.currentPage > maxPageIndex) {
              console.log(
                `Current page ${state.currentPage} exceeds max page ${maxPageIndex}, resetting to 0`
              );
              state.currentPage = 0;
            }

            state.isLoading = false;
            state.lastFetchTime = Date.now();
            state.currentOrganizationId = organizationId;
          });
        } catch (error) {
          console.error("Failed to fetch patients:", error);
          set((state) => {
            state.error = "Failed to fetch patients";
            state.isLoading = false;
          });
        }
      },

      fetchPatientsForCurrentOrg: async () => {
        const selectedOrgId =
          useOrganizationsStore.getState().selectedOrganizationId;

        if (!selectedOrgId) {
          set((state) => {
            state.error = "No organization selected";
          });
          return;
        }

        // Use v3 endpoint with regular filtering
        await get().fetchPatientsAdvanced(selectedOrgId);
      },

      addPatient: (patient) => {
        set((state) => {
          state.patients.push(patient);
        });
      },

      updatePatient: (patientId, updatedPatient) => {
        set((state) => {
          const index = state.patients.findIndex((p) => p.id === patientId);
          if (index !== -1) {
            state.patients[index] = {
              ...state.patients[index],
              ...updatedPatient,
            };
          }

          // Also update in selectedPatients if present
          const selectedIndex = state.selectedPatients.findIndex(
            (p) => p.id === patientId
          );
          if (selectedIndex !== -1) {
            state.selectedPatients[selectedIndex] = {
              ...state.selectedPatients[selectedIndex],
              ...updatedPatient,
            };
          }
        });
      },

      getPatient: (patientId) => {
        return get().patients.find((p) => p.id === patientId);
      },

      clearPatients: () => {
        set((state) => {
          state.patients = [];
          state.error = null;
        });
      },

      setCurrentPage: (page) => {
        set((state) => {
          state.currentPage = page;
        });
      },

      setSortingState: (sorting) => {
        set((state) => {
          state.sortingState = sorting;
        });
      },

      setPageSize: (size) => {
        set((state) => {
          state.pageSize = size;
        });
      },

      setFilters: (newFilters) => {
        set((state) => {
          state.filters = {
            ...state.filters,
            ...newFilters,
          };
          // Reset to first page when filters change
          state.currentPage = 0;
          // Clear smart search when regular filters are applied
          state.isSmartSearchMode = false;
          state.smartSearchQuery = null;
          state.smartSearchFilters = null;
          state.structuredFilters = null;
          state.queryStats = null;
        });
      },

      fetchPatientsV3: async (organizationId: string, v3Filters: any) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
          state.currentOrganizationId = organizationId;
        });

        try {
          const currentPage = get().currentPage;
          const pageSize = get().pageSize;

          const response = await api.getPatientsAdvancedV3(organizationId, {
            ...v3Filters,
            page: currentPage + 1, // API is 1-indexed, TanStack Table is 0-indexed
            page_size: pageSize,
          });

          set((state) => {
            state.patients = response.results;
            state.totalPatients = response.total;
            state.totalPages = response.total_pages;
            state.queryStats = {
              patient_count: response.query_summary.patient_count,
              avg_success_rate: response.query_summary.avg_success_rate,
              consent_pending_count:
                response.query_summary.consent_pending_count,
              overdue_count: response.query_summary.overdue_count,
            };

            // Ensure current page is valid for the new data set
            const maxPageIndex = Math.max(0, state.totalPages - 1);
            if (state.currentPage > maxPageIndex) {
              console.log(
                `Current page ${state.currentPage} exceeds max page ${maxPageIndex}, resetting to 0`
              );
              state.currentPage = 0;
            }

            state.isLoading = false;
            state.lastFetchTime = Date.now();
            state.currentOrganizationId = organizationId;
          });
        } catch (error) {
          console.error("Failed to fetch patients with v3:", error);
          set((state) => {
            state.error = "Failed to fetch patients";
            state.isLoading = false;
          });
        }
      },

      applySmartSearch: (query: string, v3Filters: any, results: any) => {
        set((state) => {
          state.isSmartSearchMode = true;
          state.smartSearchQuery = query;
          state.smartSearchFilters = v3Filters;
          state.patients = results.results;
          state.totalPatients = results.total;
          state.totalPages = results.total_pages;
          state.queryStats = {
            patient_count: results.query_summary.patient_count,
            avg_success_rate: results.query_summary.avg_success_rate,
            consent_pending_count: results.query_summary.consent_pending_count,
            overdue_count: results.query_summary.overdue_count,
          };
          // Clear regular filters when smart search is applied
          state.filters = {
            search: null,
            gender: null,
            tags: null,
            apcm_consent_status: null,
            latest_call_status: null,
          };
          // Reset to first page
          state.currentPage = 0;
        });
      },

      clearSmartSearch: () => {
        set((state) => {
          state.isSmartSearchMode = false;
          state.smartSearchQuery = null;
          state.smartSearchFilters = null;
          state.structuredFilters = null;
          state.queryStats = null;
          state.currentPage = 0;
        });
      },

      setSmartSearch: (query, v3Filters) => {
        set((state) => {
          state.isSmartSearchMode = true;
          state.smartSearchQuery = query;
          state.smartSearchFilters = v3Filters;
          state.structuredFilters = null;
          state.currentPage = 0;
        });
      },

      setStructuredFilters: (v3Filters) => {
        set((state) => {
          state.structuredFilters = v3Filters;
          state.isSmartSearchMode = false;
          state.smartSearchQuery = null;
          state.smartSearchFilters = null;
          state.currentPage = 0;
        });
      },

      getNextPatientId: (currentPatientId) => {
        const { patients, sortingState } = get();

        // Apply the same sorting as in the table
        let sortedPatients = [...patients];

        if (sortingState.length > 0) {
          const { id, desc } = sortingState[0];

          sortedPatients.sort((a, b) => {
            let valueA, valueB;

            if (id === "name") {
              valueA = `${a.first_name} ${a.last_name}`;
              valueB = `${b.first_name} ${b.last_name}`;
            } else {
              valueA = (a as any)[id];
              valueB = (b as any)[id];
            }

            if (valueA < valueB) return desc ? 1 : -1;
            if (valueA > valueB) return desc ? -1 : 1;
            return 0;
          });
        }

        const currentIndex = sortedPatients.findIndex(
          (p) => p.id === currentPatientId
        );
        if (currentIndex === -1 || currentIndex === sortedPatients.length - 1)
          return undefined;

        return sortedPatients[currentIndex + 1].id;
      },

      getPreviousPatientId: (currentPatientId) => {
        const { patients, sortingState } = get();

        // Apply the same sorting as in the table
        let sortedPatients = [...patients];

        if (sortingState.length > 0) {
          const { id, desc } = sortingState[0];

          sortedPatients.sort((a, b) => {
            let valueA, valueB;

            if (id === "name") {
              valueA = `${a.first_name} ${a.last_name}`;
              valueB = `${b.first_name} ${b.last_name}`;
            } else {
              valueA = (a as any)[id];
              valueB = (b as any)[id];
            }

            if (valueA < valueB) return desc ? 1 : -1;
            if (valueA > valueB) return desc ? -1 : 1;
            return 0;
          });
        }

        const currentIndex = sortedPatients.findIndex(
          (p) => p.id === currentPatientId
        );
        if (currentIndex <= 0) return undefined;

        return sortedPatients[currentIndex - 1].id;
      },

      getPageCount: () => {
        const { patients, pageSize } = get();
        return Math.ceil(patients.length / pageSize);
      },

      // Selection management functions
      togglePatientSelection: (patient) => {
        set((state) => {
          const isSelected = state.selectedPatients.some(
            (p) => p.id === patient.id
          );

          if (isSelected) {
            // Remove from selection
            state.selectedPatients = state.selectedPatients.filter(
              (p) => p.id !== patient.id
            );
          } else {
            // Add to selection
            state.selectedPatients.push(patient);
          }
        });
      },

      isPatientSelected: (patientId) => {
        return get().selectedPatients.some((p) => p.id === patientId);
      },

      removePatientSelection: (patientId) => {
        set((state) => {
          state.selectedPatients = state.selectedPatients.filter(
            (p) => p.id !== patientId
          );
        });
      },

      clearSelections: () => {
        set((state) => {
          state.selectedPatients = [];
        });
      },

      addTagsToMultiplePatients: async (
        patientIds: string[],
        tags: string[]
      ) => {
        try {
          await api.addTagsToMultiplePatients(patientIds, tags);

          // Update the patients in the store with the new tags
          set((state) => {
            // Update main patients list
            state.patients = state.patients.map((patient) => {
              if (patientIds.includes(patient.id)) {
                // Add new tags without duplicates
                const currentTags = patient.tags || [];
                const uniqueTags = Array.from(
                  new Set([...currentTags, ...tags])
                );

                return {
                  ...patient,
                  tags: uniqueTags,
                };
              }
              return patient;
            });

            // Also update selected patients
            state.selectedPatients = state.selectedPatients.map((patient) => {
              if (patientIds.includes(patient.id)) {
                const currentTags = patient.tags || [];
                const uniqueTags = Array.from(
                  new Set([...currentTags, ...tags])
                );

                return {
                  ...patient,
                  tags: uniqueTags,
                };
              }
              return patient;
            });
          });

          return Promise.resolve();
        } catch (error) {
          console.error("Failed to add tags to patients:", error);
          return Promise.reject(error);
        }
      },

      updatePatientsAfterBulkLanguageChange: (
        patientIds: string[],
        language: string
      ) => {
        set((state) => {
          // Update main patients list
          state.patients = state.patients.map((patient) => {
            if (patientIds.includes(patient.id)) {
              return {
                ...patient,
                notes: {
                  ...patient.notes,
                  preferences: {
                    ...(patient.notes?.preferences || {}),
                    language: language,
                  },
                },
              };
            }
            return patient;
          });

          // Also update selected patients
          state.selectedPatients = state.selectedPatients.map((patient) => {
            if (patientIds.includes(patient.id)) {
              return {
                ...patient,
                notes: {
                  ...patient.notes,
                  preferences: {
                    ...(patient.notes?.preferences || {}),
                    language: language,
                  },
                },
              };
            }
            return patient;
          });
        });
      },

      // Reset state when organization changes
      resetPaginationState: () => {
        set((state) => {
          state.patients = [];
          state.selectedPatients = [];
          state.isLoading = false;
          state.error = null;
          state.currentPage = 0;
          state.pageSize = 10;
          state.sortingState = [];
          state.lastFetchTime = 0;
          state.totalPatients = 0;
          state.totalPages = 0;
          state.currentOrganizationId = null;
          state.filters = {
            search: null,
            gender: null,
            tags: null,
            apcm_consent_status: null,
            latest_call_status: null,
          };
          state.isSmartSearchMode = false;
          state.smartSearchQuery = null;
          state.smartSearchFilters = null;
          state.structuredFilters = null;
          state.queryStats = null;
        });
      },
    })),
    {
      name: "patients-storage", // Name for the localStorage item
      partialize: (state) => ({
        patients: state.patients,
        selectedPatients: state.selectedPatients,
        currentPage: state.currentPage,
        sortingState: state.sortingState,
        lastFetchTime: state.lastFetchTime,
        currentOrganizationId: state.currentOrganizationId,
        pageSize: state.pageSize,
        filters: state.filters,
        isSmartSearchMode: state.isSmartSearchMode,
        smartSearchQuery: state.smartSearchQuery,
        smartSearchFilters: state.smartSearchFilters,
        structuredFilters: state.structuredFilters,
        queryStats: state.queryStats,
      }),
    }
  )
);
